import React, { useMemo, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import MetricCard from '../components/MetricCard';
import ImpactActionDetailDrawer from '../components/drawers/ImpactActionDetailDrawer';
import { AIOutlineButton } from '../components/ui/AIOutlineButton';
import { AISidebar } from '../components/AISidebar';
import { AIRecommendationPanel } from '../components/ai/AIRecommendationPanel';
import { getImpactIndicesRecommendations } from '../utils/aiLMSRecommendations';
import { useAdminData } from '../contexts/AdminDataContext';
import { Leaf, Droplet, TreePine, Filter } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
type PeriodFilter = '7days' | '30days' | '90days' | 'thisMonth';
const ImpactIndices = () => {
  const {
    impactData
  } = useAdminData();
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('30days');
  const [categoryFilter, setCategoryFilter] = useState<string>('Tutte');
  const [selectedAction, setSelectedAction] = useState<any>(null);
  const [showActionDrawer, setShowActionDrawer] = useState(false);
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(false);
  const recommendations = getImpactIndicesRecommendations().map(rec => ({
    ...rec,
    onCtaClick: () => console.log('CTA clicked:', rec.id)
  }));
  // Get unique categories
  const categories = useMemo(() => {
    const cats = impactData.topActions.map(a => a.category);
    return ['Tutte', ...Array.from(new Set(cats))];
  }, [impactData]);
  // Filter data based on period
  const filteredData = useMemo(() => {
    const today = new Date();
    let daysToShow = 30;
    switch (periodFilter) {
      case '7days':
        daysToShow = 7;
        break;
      case '30days':
        daysToShow = 30;
        break;
      case '90days':
        daysToShow = 90;
        break;
      case 'thisMonth':
        daysToShow = today.getDate();
        break;
    }
    const cutoffDate = new Date(today);
    cutoffDate.setDate(cutoffDate.getDate() - daysToShow);
    const filteredSeries = impactData.impactSeries.filter(item => new Date(item.date) >= cutoffDate);
    // Calculate KPIs from filtered series
    const totalCO2 = filteredSeries.reduce((sum, item) => sum + item.co2, 0);
    const totalPlastic = filteredSeries.reduce((sum, item) => sum + item.plastic, 0);
    const totalTrees = filteredSeries.reduce((sum, item) => sum + item.trees, 0);
    return {
      impactSeries: filteredSeries,
      kpis: {
        co2Saved: totalCO2,
        plasticAvoided: totalPlastic,
        treesPlanted: totalTrees
      }
    };
  }, [periodFilter, impactData]);
  // Filter actions by category
  const filteredActions = useMemo(() => {
    if (categoryFilter === 'Tutte') {
      return impactData.topActions;
    }
    return impactData.topActions.filter(action => action.category === categoryFilter);
  }, [categoryFilter, impactData]);
  const handleActionClick = (action: any) => {
    setSelectedAction(action);
    setShowActionDrawer(true);
  };
  const handleCloseActionDrawer = () => {
    setShowActionDrawer(false);
    setTimeout(() => setSelectedAction(null), 300);
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-2254">
      <div className="w-20 flex-shrink-0" data-id="element-2255">
        <Sidebar data-id="element-2256" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-2257">
        <Topbar data-id="element-2258" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-2259">
          {/* Header with filters and AI button */}
          <div className="flex items-start justify-between mb-6" data-id="element-2260">
            <div data-id="element-2261">
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2262">
                Indici di Impatto
              </h1>
              <p className="text-sm font-normal text-gray-500 mt-1" data-id="element-2263">
                Monitora l'impatto ambientale positivo della community
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-2264">
              <select value={periodFilter} onChange={e => setPeriodFilter(e.target.value as PeriodFilter)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-normal focus:outline-none focus:ring-2 focus:ring-green-600" data-id="element-2265">
                <option value="7days" data-id="element-2266">Ultimi 7 giorni</option>
                <option value="30days" data-id="element-2267">Ultimi 30 giorni</option>
                <option value="90days" data-id="element-2268">Ultimi 90 giorni</option>
                <option value="thisMonth" data-id="element-2269">Questo mese</option>
              </select>
              <AIOutlineButton onClick={() => setIsAISidebarOpen(true)} data-id="element-2270">
                Suggerimenti AI
              </AIOutlineButton>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-2271">
            <MetricCard title="CO₂ Risparmiata" value={`${filteredData.kpis.co2Saved.toLocaleString()} kg`} change="18.5%" isPositive={true} icon={Leaf} data-id="element-2272" />
            <MetricCard title="Plastica Evitata" value={`${filteredData.kpis.plasticAvoided.toLocaleString()} kg`} change="22.3%" isPositive={true} icon={Droplet} data-id="element-2273" />
            <MetricCard title="Alberi Piantati" value={filteredData.kpis.treesPlanted.toLocaleString()} change="12.7%" isPositive={true} icon={TreePine} data-id="element-2274" />
          </div>

          {/* Charts Row */}
          <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-2275">
            {/* Impact Over Time Chart */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm" data-id="element-2276">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-2277">
                Impatto nel Tempo
              </h3>
              <div className="h-80" data-id="element-2278">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2279">
                  <LineChart data={filteredData.impactSeries} data-id="element-2280">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-2281" />
                    <XAxis dataKey="date" stroke="#6B7280" fontSize={12} tickFormatter={value => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }} data-id="element-2282" />
                    <YAxis stroke="#6B7280" fontSize={12} data-id="element-2283" />
                    <Tooltip contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.5rem'
                  }} labelFormatter={value => {
                    const date = new Date(value);
                    return date.toLocaleDateString('it-IT');
                  }} data-id="element-2284" />
                    <Legend data-id="element-2285" />
                    <Line type="monotone" dataKey="co2" stroke="#108d3e" strokeWidth={2} dot={false} name="CO₂ (kg)" data-id="element-2286" />
                    <Line type="monotone" dataKey="plastic" stroke="#3B82F6" strokeWidth={2} dot={false} name="Plastica (kg)" data-id="element-2287" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Impact by Category Chart */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm" data-id="element-2288">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-2289">
                Impatto per Categoria
              </h3>
              <div className="h-80" data-id="element-2290">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2291">
                  <BarChart data={impactData.categoryData} data-id="element-2292">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-2293" />
                    <XAxis dataKey="category" stroke="#6B7280" fontSize={12} data-id="element-2294" />
                    <YAxis stroke="#6B7280" fontSize={12} data-id="element-2295" />
                    <Tooltip contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.5rem'
                  }} formatter={(value: any) => [`${value.toLocaleString()} kg CO₂`, 'Impatto']} data-id="element-2296" />
                    <Bar dataKey="value" fill="#108d3e" radius={[4, 4, 0, 0]} data-id="element-2297" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Top Actions Table */}
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden" data-id="element-2298">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-2299">
              <div data-id="element-2300">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-2301">
                  Top Azioni Impatto
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2302">
                  Le azioni con maggiore impatto ambientale
                </p>
              </div>
              <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2" data-id="element-2303">
                <Filter className="mr-2 h-4 w-4 text-gray-500" data-id="element-2304" />
                <select className="bg-transparent text-sm focus:outline-none" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} data-id="element-2305">
                  {categories.map(cat => <option key={cat} value={cat} data-id="element-2306">
                      {cat}
                    </option>)}
                </select>
              </div>
            </div>
            <div className="overflow-x-auto" data-id="element-2307">
              <table className="w-full" data-id="element-2308">
                <thead data-id="element-2309">
                  <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2310">
                    <th className="px-6 py-3" data-id="element-2311">Azione</th>
                    <th className="px-6 py-3" data-id="element-2312">Categoria</th>
                    <th className="px-6 py-3" data-id="element-2313">Completamenti</th>
                    <th className="px-6 py-3" data-id="element-2314">CO₂ Stimata</th>
                    <th className="px-6 py-3" data-id="element-2315">Ultimo Aggiornamento</th>
                    <th className="px-6 py-3 text-right" data-id="element-2316">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-2317">
                  {filteredActions.map(action => <tr key={action.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleActionClick(action)} data-id="element-2318">
                      <td className="px-6 py-4" data-id="element-2319">
                        <div className="font-medium text-gray-900" data-id="element-2320">
                          {action.name}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-2321">
                        <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2322">
                          {action.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-2323">
                        {action.completions.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 font-medium text-green-600" data-id="element-2324">
                        {action.co2.toLocaleString()} kg
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-2325">
                        {action.lastUpdated}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-2326">
                        <button onClick={e => {
                      e.stopPropagation();
                      handleActionClick(action);
                    }} className="text-green-600 hover:text-green-700 font-medium text-sm" data-id="element-2327">
                          Dettagli
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* AI Sidebar */}
      <AISidebar isOpen={isAISidebarOpen} onClose={() => setIsAISidebarOpen(false)} title="Suggerimenti AI: Indici di Impatto" data-id="element-2328">
        <AIRecommendationPanel recommendations={recommendations} data-id="element-2329" />
      </AISidebar>

      {/* Impact Action Detail Drawer */}
      <ImpactActionDetailDrawer isOpen={showActionDrawer} onClose={handleCloseActionDrawer} action={selectedAction} data-id="element-2330" />
    </div>;
};
export default ImpactIndices;